/**
 * Mailer Campaign List - Column & filter configuration
 */
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import {
    createColumns,
    createDateFormatter,
} from '@/components/ui/data-table-customized'
import type { MailerCampaign } from '../types'
import type { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import { generatePathDatacentral, paths } from '@/utils/path'

// ============================================================================
// Status chip color map
// ============================================================================

export const MAILER_CAMPAIGN_STATUS_COLOR_MAP = {
    draft: { label: 'draft', color: 'default' as const },
    processing: { label: 'processing', color: 'info' as const },
    completed: { label: 'completed', color: 'success' as const },
    cancelled: { label: 'cancelled', color: 'error' as const },
}

// ============================================================================
// Columns
// ============================================================================

export const getMailerCampaignColumns = (
    t: TFunction
): DataTableColumn<MailerCampaign>[] =>
    createColumns<MailerCampaign>([
        {
            key: 'mailer_campaign_id',
            title: 'ID',
            width: 80,
            sortable: false,
            render: (_, row) => (
                <Link
                    to={generatePathDatacentral(paths.mailerCampaignDetail, {
                        id: row.mailer_campaign_id,
                    })}
                    className="text-primary hover:underline"
                >
                    {row.mailer_campaign_id}
                </Link>
            ),
        },
        {
            key: 'mailer_campaign_name',
            title: t('mailerCampaign:campaign_name'),
            sortable: false,
        },
        {
            key: 'mailer_template_subject',
            title: t('mailerCampaign:template_subject'),
        },
        {
            key: 'mailer_campaign_type',
            title: t('type'),
            formatter: {
                type: 'chip',
                colorMap: {
                    service: { label: 'service', color: 'info' },
                    marketing: { label: 'marketing', color: 'warning' },
                    campaign: { label: 'campaign', color: 'success' },
                    simple: { label: 'simple', color: 'default' },
                    system: { label: 'system', color: 'secondary' },
                    event: { label: 'event', color: 'info' },
                },
            },
        },
        {
            key: 'mailer_campaign_source_type',
            title: t('mailerCampaign:source_type'),
            formatter: {
                type: 'chip',
                colorMap: {
                    internal: { label: 'internal', color: 'default' },
                    external: { label: 'external', color: 'warning' },
                },
            },
        },
        {
            key: 'mailer_campaign_status',
            title: t('status'),
            formatter: {
                type: 'chip',
                colorMap: {
                    draft: { label: 'draft', color: 'default' },
                    processing: { label: 'processing', color: 'info' },
                    completed: { label: 'completed', color: 'success' },
                    cancelled: { label: 'cancelled', color: 'error' },
                },
            },
        },
        {
            key: 'mailer_campaign_created_at',
            title: t('created_at'),
            formatter: createDateFormatter({ format: 'DD/MM/YYYY HH:mm' }),
        },
        {
            key: 'mailer_campaign_started_at',
            title: t('mailerCampaign:started_at'),
            formatter: createDateFormatter({ format: 'DD/MM/YYYY HH:mm' }),
        },
        {
            key: 'mailer_campaign_completed_at',
            title: t('mailerCampaign:completed_at'),
            formatter: createDateFormatter({ format: 'DD/MM/YYYY HH:mm' }),
        },
    ])

// ============================================================================
// Sort & filter configuration
// ============================================================================

export const MAILER_CAMPAIGN_SORT_CONFIG = {
    defaultSort: {
        sort_by: 'mailer_campaign_id',
        sort_order: 'desc' as const,
    },
}

export const MAILER_CAMPAIGN_FILTER_DEFAULTS = {
    q: '',
}
